import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Icon, Button, Spin, Tabs, Breadcrumb, Select, DatePicker } from 'antd';
import moment from 'moment';
import decode from 'jwt-decode';
import * as XLSX from 'xlsx';
import * as hostReportActions from '../../../redux/hostReport/hostReportActions';
import { checkUserAuthorization } from '../../HostDashboard/HostDashboardActions';
import BookingsTable from './BookingsTable';
import BlockedByHostTable from './BlockedByHostTable';
import GoogleCalendarTable from './GoogleCalendarTable';
import '../../../assets/scss/HostReport.scss';
import useDashboardMarket from '../../../hooks/useDashboardMarket';

const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

const HostReport = () => {
    const dispatch = useDispatch();
    const marketCountry = useDashboardMarket();
    
    // Select state from Redux
    const {
        liveCounts,
        liveCountsLoading,
        stats,
        statsLoading,
        activeTab,
        loading,
        dataSource: bookingsData,
        blockedDataSource: blockedData,
        googleDataSource: googleData,
        spaceList
    } = useSelector(state => state.hostReport);

    const [dateFilter, setDateFilter] = useState('thisyear');
    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const [customDateRange, setCustomDateRange] = useState(null);
    const [exporting, setExporting] = useState(false);
    
    // State to hold filtered data from child components for export
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [filteredBlocked, setFilteredBlocked] = useState([]);
    const [filteredGoogle, setFilteredGoogle] = useState([]);
    
    const authStatus = useSelector(state => state?.hostDashboard?.calendar?.authStatus);

    const authToken = useSelector((state) => state?.auth?.loginDetails?.data?.token);

    const disabledDate = (current) => {
        // Disable dates more than 5 years ago or in future if needed (AdminDashboard rule: 5 years ago to future allowed up to today?)
        // AdminDashboard: current.isBefore(fiveYearsAgo) || current.isAfter(moment())
        // For Host Report, future bookings are valid, so maybe just limit past?
        // Let's stick to safe limits or just copy Admin Dashboard if appropriate.
        // Actually, for bookings, we want to see future too. 
        // AdminDashboard limits to "Today" end for metrics, but Host Report might want future bookings.
        // But the user said "like admin dashboard styling". I will stick to styling props.
        // If I copy logic: `current.isBefore(fiveYearsAgo) || current.isAfter(moment())` 
        // this would prevent selecting future dates for "Upcoming Bookings".
        // So I will NOT disable future dates. Just styling.
        const fiveYearsAgo = moment().subtract(5, 'years');
        return current && current.isBefore(fiveYearsAgo);
    };

    const getDateRange = () => {
        let startDate, endDate;
        if (dateFilter === 'today') {
            startDate = moment().format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
        } else if (dateFilter === '7days') {
            startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
        } else if (dateFilter === '30days') {
            startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
        } else if (dateFilter === '90days') {
            startDate = moment().subtract(90, 'days').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
        } else if (dateFilter === 'thisyear') {
            startDate = moment().startOf('year').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
        } else if (dateFilter === 'custom' && customDateRange && customDateRange.length === 2 && customDateRange[0] && customDateRange[1]) {
            startDate = customDateRange[0].format('YYYY-MM-DD');
            endDate = customDateRange[1].format('YYYY-MM-DD');
        }
        return { startDate, endDate };
    };

    useEffect(() => {
        dispatch(hostReportActions.getLiveCounts());
        dispatch(checkUserAuthorization());
    }, []);

    useEffect(() => {
        if (marketCountry) {
            dispatch(hostReportActions.getHostSpaces(marketCountry));
        }
    }, [dispatch, marketCountry]);

    useEffect(() => {
        const { startDate, endDate } = getDateRange();
        if (startDate && endDate && marketCountry) {
            const rangeString = `${startDate},${endDate}`;
            dispatch(hostReportActions.getHostReportStats(rangeString));
            // Fetch ALL records for each table type — table-level filtering is client-side
            dispatch(hostReportActions.getHostReportData(rangeString, marketCountry));
            dispatch(hostReportActions.getBlockedReportData(rangeString, marketCountry));
        }
    }, [dateFilter, customDateRange, marketCountry, dispatch]);

    useEffect(() => {
        const { startDate, endDate } = getDateRange();
        if (startDate && endDate && authStatus && authStatus.status && marketCountry) {
            const rangeString = `${startDate},${endDate}`;
            dispatch(hostReportActions.getGoogleReportData(rangeString, marketCountry));
        }
    }, [dateFilter, customDateRange, authStatus, marketCountry, dispatch]);

    const handleExport = () => {
        setExporting(true);
        setTimeout(() => {
            try {
                const wb = XLSX.utils.book_new();

                // 1. Bookings Sheet (Use filteredBookings if available, else bookingsData - though filteredBookings should match)
                const dataToExport1 = filteredBookings && filteredBookings.length > 0 ? filteredBookings : (bookingsData || []);
                const bookingsSheetData = [
                    ['Booking ID', 'Space Name', 'Guest Name', 'Guest Email', 'Status', 'Start Date', 'Start Time', 'End Date', 'End Time', 'Amount', 'Blocked By']
                ];
                (dataToExport1 || []).forEach(b => {
                    bookingsSheetData.push([
                        b.bookingId,
                        b.spaceName,
                        b.userName, // guest name
                        b.userEmail,
                        b.status,
                        b.eventStartDate,
                        b.eventStartTime,
                        b.eventEndDate,
                        b.eventEndTime,
                        b.amount,
                        b.blockedBy || '-'
                    ]);
                });
                const ws1 = XLSX.utils.aoa_to_sheet(bookingsSheetData);
                // Simple bold header
                if(ws1['A1']) ws1['A1'].s = { font: { bold: true } };
                XLSX.utils.book_append_sheet(wb, ws1, "Bookings");

                // 2. Blocked By Host Sheet
                const dataToExport2 = filteredBlocked && filteredBlocked.length > 0 ? filteredBlocked : (blockedData || []);
                const blockedSheetData = [
                    ['Space Name', 'Guest Name', 'Guest Email', 'Start Date', 'Start Time', 'End Date', 'End Time']
                ];
                (dataToExport2 || []).forEach(b => {
                    blockedSheetData.push([
                        b.spaceName,
                        b.userName,
                        b.userEmail,
                        b.eventStartDate,
                        b.eventStartTime,
                        b.eventEndDate,
                        b.eventEndTime
                    ]);
                });
                const ws2 = XLSX.utils.aoa_to_sheet(blockedSheetData);
                XLSX.utils.book_append_sheet(wb, ws2, "Blocked By Host");

                // 3. Google Calendar Sheet
                if (authStatus && authStatus.status) {
                    const dataToExport3 = filteredGoogle && filteredGoogle.length > 0 ? filteredGoogle : (googleData || []);
                    const googleSheetData = [
                         ['Space Name', 'Title', 'Start Date', 'Start Time', 'End Date', 'End Time', 'Status']
                    ];
                     (dataToExport3 || []).forEach(g => {
                        googleSheetData.push([
                            g.spaceName,
                            g.title,
                            g.eventStartDate,
                            g.eventStartTime,
                            g.eventEndDate,
                            g.eventEndTime,
                            g.status
                        ]);
                    });
                    const ws3 = XLSX.utils.aoa_to_sheet(googleSheetData);
                    XLSX.utils.book_append_sheet(wb, ws3, "Google Calendar");
                }

                XLSX.writeFile(wb, `Host_Report_${moment().format('YYYYMMDD_HHmm')}.xlsx`);
            } catch (error) {
                console.error("Export failed", error);
            } finally {
                setExporting(false);
            }
        }, 100);
    };

    const handleDateFilterChange = (value) => {
        setDateFilter(value);
        if (value !== 'custom') {
            setCustomDateRange(null);
        }
    };

    const handleCustomDateChange = (dates) => {
        setCustomDateRange(dates);
    };

    const handleSpaceChange = (values) => {
        setSelectedSpaces(values);
    };

  return (
      <div className="report-container-wrapper host-report-page">
          <div className="dashboard-header">
                <h1>
                  Reports
                </h1>
                <div className="dashboard-controls">
                    <Select
                        className="space-filter"
                        dropdownClassName="host-report-space-dropdown"
                        mode="multiple"
                        allowClear
                        placeholder="Search Spaces"
                        onChange={handleSpaceChange}
                        value={selectedSpaces}
                        maxTagCount={1}
                        filterOption={(input, option) =>
                            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {spaceList && spaceList.map(space => (
                            <Option key={space.id} value={space.id}>
                                {space.spaceName}
                            </Option>
                        ))}
                    </Select>
                    <Select className="date-filter" value={dateFilter} onChange={handleDateFilterChange} style={{ width: 150 }}>
                        <Option value="today">Today</Option>
                        <Option value="7days">Last 7 Days</Option>
                        <Option value="30days">Last 30 Days</Option>
                        <Option value="90days">Last 90 Days</Option>
                        <Option value="thisyear">This Year</Option>
                        <Option value="custom">Custom Range</Option>
                    </Select>
                    {dateFilter === 'custom' && (
                        <RangePicker 
                            value={customDateRange} 
                            onChange={handleCustomDateChange} 
                            style={{ width: 250 }}
                            disabledDate={disabledDate}
                            placeholder={['Start Date', 'End Date']}
                            dropdownClassName="host-report-datepicker-dropdown"
                            format="YYYY-MM-DD"
                        />
                    )}
                    <Button 
                        className="export-btn" 
                        onClick={handleExport}
                        loading={exporting}
                        disabled={exporting || (dateFilter === 'custom' && (!customDateRange || customDateRange.length < 2))}
                    >
                        {!exporting && <Icon type="download" style={{ marginRight: 8 }} />} 
                        {exporting ? 'Exporting...' : 'Export Report'}
                    </Button>
                </div>
          </div>
            <div className="dashboard-content">
              
              <div className="dashboard-section" style={{ marginBottom: 0, padding: 0 }}>
                <h3>Overview (Selected Period)</h3>
              </div>
              <Spin spinning={statsLoading} indicator={<Icon type="loading" style={{ fontSize: 24, color: '#e91e63' }} spin />}>
                <div className="analytics-grid cols-5" style={{ marginBottom: '30px' }}>
                  <div className="stat-card">
                    <div className="stat-content">
                      <span className="stat-title">Total Bookings</span>
                      <div className="stat-value">
                        {stats.totalBookings}
                      </div>
                    </div>
                    <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(24, 144, 255, 0.1)', color: '#1890ff' }}>
                      <Icon type="bars" />
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-content">
                      <span className="stat-title">Confirmed</span>
                      <div className="stat-value">
                        {stats.confirmedBookings}
                      </div>
                    </div>
                    <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(82, 196, 26, 0.1)', color: '#52c41a' }}>
                      <Icon type="check-circle" />
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-content">
                      <span className="stat-title">Cancelled</span>
                      <div className="stat-value">
                        {stats.cancelledBookings}
                      </div>
                    </div>
                     <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(255, 77, 79, 0.1)', color: '#ff4d4f' }}>
                      <Icon type="close-circle" />
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-content">
                      <span className="stat-title">Tentative</span>
                      <div className="stat-value">
                        {stats.tentativeBookings}
                      </div>
                    </div>
                    <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(250, 173, 20, 0.1)', color: '#faad14' }}>
                      <Icon type="clock-circle" />
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-content">
                      <span className="stat-title">Past Bookings</span>
                      <div className="stat-value">
                        {stats.pastBookings}
                      </div>
                    </div>
                    <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(114, 46, 209, 0.1)', color: '#722ed1' }}>
                      <Icon type="history" />
                    </div>
                  </div>
                </div>
              </Spin>

              <div className="dashboard-section" style={{ marginBottom: 0, padding: 0 }}>
                <h3>Live Booking Counts (Real-time)</h3>
              </div>
              <Spin spinning={liveCountsLoading} indicator={<Icon type="loading" style={{ fontSize: 24, color: '#e91e63' }} spin />}>
                <div className="analytics-grid live-counts-grid" style={{ marginBottom: '30px' }}>
                  <div className="stat-card">
                    <div className="stat-content">
                      <span className="stat-title">Ongoing Bookings</span>
                      <div className="stat-value">
                        {liveCounts.ongoing}
                      </div>
                    </div>
                    <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(24, 144, 255, 0.1)', color: '#1890ff' }}>
                      <Icon type="sync" spin />
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-content">
                      <span className="stat-title">Upcoming Bookings</span>
                      <div className="stat-value">
                        {liveCounts.upcoming}
                      </div>
                    </div>
                    <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(82, 196, 26, 0.1)', color: '#52c41a' }}>
                      <Icon type="calendar" />
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-content">
                      <span className="stat-title">Tentative Bookings</span>
                      <div className="stat-value">
                        {liveCounts.tentative}
                      </div>
                    </div>
                    <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(250, 173, 20, 0.1)', color: '#faad14' }}>
                      <Icon type="clock-circle" />
                    </div>
                  </div>
                </div>
              </Spin>
              
              <div className="dashboard-section" style={{ marginBottom: 20 }}>
                <h3>Bookings</h3>
                <BookingsTable onDataChange={setFilteredBookings} selectedSpaces={selectedSpaces} />
              </div>
              
              <div className="dashboard-section" style={{ marginBottom: 20 }}>
                <h3>Blocked By Host</h3>
                <BlockedByHostTable onDataChange={setFilteredBlocked} selectedSpaces={selectedSpaces} />
              </div>

              {authStatus && authStatus.status && (
                  <div className="dashboard-section" style={{ marginBottom: 20 }}>
                    <h3>Blocked By Google Calendar</h3>
                    <GoogleCalendarTable onDataChange={setFilteredGoogle} selectedSpaces={selectedSpaces} />
                  </div>
              )}
          </div>

      </div>
  );
};

export default HostReport;